import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './WorkerSoldOut.module.scss';
import AvatarIcon from '../../../../Common/AvatarIcon';
import { ReactComponent as LogoIcon } from '../../../../../assets/svg/logo_noText.svg';
import { ReactComponent as XMasFrame } from '../../../../../assets/svg/xMas_frame.svg';
import { useDispatch, useSelector } from 'react-redux';
import clx from 'classnames';
import dayjs from 'dayjs';
import { getCalendarSlots, getCalendarWorkerServices } from 'Containers/Scheduler/store/selectors';
import { schedulerActions } from 'Containers/Scheduler/store';
import { useTranslation } from 'react-i18next';
import { Flame, Share2, X, Sparkles, Rocket } from 'lucide-react';
import useWindowSize from 'utils/useWindowSize';
import { toastActions } from 'Containers/Toasts/store';
import {
  canvasToShareFile,
  composeStoryShareCanvasFromNode,
  runStoryShareFlow,
} from 'utils/storyShare';

const EMPTY_SLOTS = [];
const EMPTY_TIMES = [];

const CardDisponibili = ({ date, timesFreeTask, worker }) => {
  const { t } = useTranslation();
  const slicedTimes =
    timesFreeTask.length > 0
      ? timesFreeTask.slice(0, 6)?.map((el) => ({
          oraInizio: el.oraInizio.split(':')[0] + ':' + el.oraInizio.split(':')[1],
          oraFine: el.oraFine.split(':')[0] + ':' + el.oraFine.split(':')[1],
          isBooked: el.isBooked,
        }))
      : [];

  const xMas = new Date(date).getMonth() == 11 && new Date(date).getDate() >= 7;

  return (
    <>
      <header className={styles.head}>
        {worker.icon ? (
          <div className={styles.avatar_wrap}>
            <AvatarIcon
              icon={worker?.icon}
              name={worker.workerName}
              size={'55px'}
              style={{ position: 'absolute' }}
            />
            {xMas && <XMasFrame width={'60px'} height={'65px'} style={{ position: 'absolute' }} />}
          </div>
        ) : null}
        <h1 className={styles.text}>{worker.workerName}</h1>
      </header>
      <div>
        <h1 className={styles.big_text}>{t('scheduler.workerSoldOutModal.availability')}</h1>
        <h1 className={clx(styles.big_text, { [styles.gold]: !!date })}>
          {dayjs().isSame(dayjs(date), 'day')
            ? t('scheduler.workerSoldOutModal.today')
            : dayjs(date).format('DD MMMM')}
        </h1>
      </div>
      <article className={styles.times_grid}>
        {slicedTimes.map((item, index) => (
          <div key={index} className={styles.time_card}>
            <h3 className={styles.text}>
              {item.oraInizio} - {item.oraFine}
            </h3>
          </div>
        ))}
      </article>
    </>
  );
};

const CardSoldOut = ({ worker, date }) => {
  const { t } = useTranslation();
  const xMas = new Date(date).getMonth() == 11 && new Date(date).getDate() >= 7;
  const isToday = dayjs().isSame(dayjs(date), 'day');

  return (
    <>
      <header className={clx(styles.head, styles.head_column)}>
        {worker.icon ? (
          <div className={styles.avatar_wrap_lg}>
            <AvatarIcon
              icon={worker?.icon}
              name={worker.workerName}
              size={'110px'}
              style={{ position: 'absolute' }}
            />
            {xMas && (
              <XMasFrame width={'125px'} height={'127px'} style={{ position: 'absolute' }} />
            )}
          </div>
        ) : null}
        <h1 className={styles.text}>{worker.workerName}</h1>
      </header>
      <div className={styles.soldout_copy}>
        <span className={styles.soldout_kicker}>
          {t('scheduler.workerSoldOutModal.availability')}
        </span>
        <h1 className={clx(styles.soldout_date, { [styles.soldout_date_long]: !isToday })}>
          {isToday ? t('scheduler.workerSoldOutModal.today') : dayjs(date).format('DD MMMM')}
        </h1>
        <span className={styles.soldout_badge}>
          <Flame size={18} strokeWidth={2.5} aria-hidden />
          {t('scheduler.workerSoldOutModal.soldOut')} {t('scheduler.workerSoldOutModal.soldOutOut')}
        </span>
      </div>
      <div className={styles.app_message_wrap}>
        <h2 className={styles.app_message}>{t('scheduler.workerSoldOutModal.appMessage')}</h2>
      </div>
    </>
  );
};

const WorkerSoldOutModal = ({ closeModal, modalData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const { worker, day, soldOut } = modalData;
  const captureRef = useRef(null);
  const slotsRequested = useRef(false);
  const shareFileRef = useRef(null);
  const shareFilePromiseRef = useRef(null);
  const services = useSelector(getCalendarWorkerServices()) || EMPTY_SLOTS;
  const [sharing, setSharing] = useState(false);

  const defaultService = useMemo(() => {
    let find = services.filter(
      (service) =>
        +service.worker_id === +worker.id && service.durata <= 30 && service.disabilitato == 0,
    );
    if (find.length == 0) {
      find = services.filter(
        (service) =>
          +service.worker_id === +worker.id && service.durata > 30 && service.disabilitato == 0,
      );
    }
    return [...find].sort((a, b) => b.durata - a.durata)[0];
  }, [services, worker.id]);

  const timesTask = useSelector(getCalendarSlots()) || EMPTY_SLOTS;
  const timesFreeTask = useMemo(() => {
    if (soldOut || timesTask.length === 0) return EMPTY_TIMES;
    const filterForDate = timesTask.find((time) => time.data === day.date)?.orari;
    return filterForDate?.filter((time) => time.isBooked == false) ?? EMPTY_TIMES;
  }, [soldOut, timesTask, day.date]);

  const xMas = new Date(day.date).getMonth() == 11 && new Date(day.date).getDate() >= 7;

  useEffect(() => {
    if (soldOut || slotsRequested.current || timesTask.length > 0 || !defaultService) return;
    slotsRequested.current = true;
    dispatch(
      schedulerActions.GET_CALENDAR_SLOTS.REQUEST({
        servizi: [defaultService],
        idPoltrona: worker.id,
        nGiorniPrenotabili: 30,
        lang: 'it',
      }),
    );
  }, [soldOut, timesTask.length, defaultService, dispatch, worker.id]);

  const handleClose = () => {
    dispatch(schedulerActions.GET_CALENDAR_SLOTS.CLEAR());
    closeModal?.();
  };

  const buildShareFile = async () => {
    if (!captureRef.current) {
      throw new Error('Capture node missing');
    }

    const baseName = soldOut ? 'sold-out' : 'disponibilita';
    const storyCanvas = await composeStoryShareCanvasFromNode(captureRef.current);
    return canvasToShareFile(storyCanvas, baseName);
  };

  const ensureShareFile = () => {
    if (shareFileRef.current) return Promise.resolve(shareFileRef.current);
    if (!shareFilePromiseRef.current) {
      shareFilePromiseRef.current = buildShareFile()
        .then((file) => {
          shareFileRef.current = file;
          return file;
        })
        .catch((err) => {
          shareFilePromiseRef.current = null;
          throw err;
        });
    }
    return shareFilePromiseRef.current;
  };

  // Prefetch dopo layout + immagini (non a 120ms: spesso catturava solo l’avatar)
  const cardReady = soldOut || timesFreeTask.length > 0;
  useEffect(() => {
    if (!cardReady) return undefined;
    shareFileRef.current = null;
    shareFilePromiseRef.current = null;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      ensureShareFile().catch(() => {});
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cardReady, soldOut, day.date, worker.id, timesFreeTask.length]);

  const showShareToast = (title, warning = true) => {
    dispatch(
      toastActions.OPEN_TOAST.SUCCESS({
        type: 'worker',
        props: { title, warning },
      }),
    );
  };

  const handleShare = async () => {
    if (sharing) return;

    try {
      setSharing(true);
      // Rigenera sempre: evita cache prefetch incompleta (solo avatar / CORS).
      shareFileRef.current = null;
      shareFilePromiseRef.current = null;
      const file = await ensureShareFile();
      const outcome = await runStoryShareFlow({
        file,
        isMobile,
        onToast: (kind) => {
          showShareToast(
            kind === 'retry'
              ? t('scheduler.workerSoldOutModal.shareRetryHint')
              : kind === 'photos'
                ? t('scheduler.workerSoldOutModal.shareSaveToPhotosHint')
                : t('scheduler.workerSoldOutModal.shareSavedFallback'),
            false,
          );
        },
      });

      if (outcome === 'shared' || outcome === 'downloaded') {
        handleClose();
      }
    } catch (err) {
      console.warn('WorkerSoldOut share failed', err);
      showShareToast(t('scheduler.workerSoldOutModal.shareError'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.header_text}>
          <span className={styles.eyebrow}>
            {soldOut ? (
              <Rocket size={14} strokeWidth={2.5} aria-hidden />
            ) : (
              <Sparkles size={14} strokeWidth={2.5} aria-hidden />
            )}
            {soldOut
              ? t('scheduler.workerSoldOutModal.eyebrowSoldOut')
              : t('scheduler.workerSoldOutModal.eyebrowAvailable')}
          </span>
          <h2 className={styles.title}>{t('scheduler.workerSoldOutModal.modalTitle')}</h2>
          <p className={styles.subtitle}>
            {soldOut
              ? t('scheduler.workerSoldOutModal.modalSubtitleSoldOut')
              : t('scheduler.workerSoldOutModal.modalSubtitleAvailable')}
          </p>
        </div>
        <button
          type="button"
          className={styles.close_btn}
          onClick={handleClose}
          aria-label={t('common.actions.close', { defaultValue: 'Chiudi' })}
        >
          <X size={18} strokeWidth={2.25} aria-hidden />
        </button>
      </div>

      <div className={styles.preview}>
        <div className={styles.preview_frame}>
          <div
            ref={captureRef}
            id="soldout-capture"
            className={clx(styles.card, {
              [styles.card_xmas]: xMas,
            })}
          >
            {!soldOut ? (
              <CardDisponibili date={day.date} worker={worker} timesFreeTask={timesFreeTask} />
            ) : (
              <CardSoldOut worker={worker} date={day.date} />
            )}
            <footer className={styles.foot} style={{ color: xMas ? '#1D5E62' : '#ffffff' }}>
              <LogoIcon fill="currentColor" aria-hidden />
              <div className={styles.foot_brand} style={{ color: 'inherit' }}>
                <span>{t('scheduler.workerSoldOutModal.bookWith')}</span>
                &nbsp;
                <strong>{t('scheduler.workerSoldOutModal.zetabarber')}</strong>
              </div>
            </footer>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.share_btn}
          onPointerDown={() => {
            ensureShareFile().catch(() => {});
          }}
          onClick={handleShare}
          disabled={sharing || (!soldOut && timesTask.length === 0)}
        >
          <Share2 size={18} strokeWidth={2.25} aria-hidden />
          <span>
            {sharing
              ? t('scheduler.workerSoldOutModal.preparing')
              : t('scheduler.workerSoldOutModal.shareCta')}
          </span>
        </button>
      </div>
    </div>
  );
};

export default WorkerSoldOutModal;
